<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import { useActionPointStore } from '@/stores/actionPoint';
import { defaultImportOptions, translatedImportOptionHeaders, type ImportOptionHeaders, type ImportOptions } from '@/types/actionPoint';
import toast from '@/utils/toast';
import { required } from '@/utils/validation';
import { computed, ref, watch } from 'vue';
import type { VForm } from 'vuetify/components';

const props = defineProps<{
    organizationId: string;
}>();

const actionPointStore = useActionPointStore();

const isDialogOpen = defineModel<boolean>();

const file = ref<File>();
const form = ref<VForm>();
const importingActionPoints = ref(false);
const options = ref<ImportOptions>(structuredClone(defaultImportOptions));

const emit = defineEmits(['done']);

const requiredColumns = computed(() =>
    Object.values(options.value.headers)
        .filter((h) => h)
        .join(', ')
);

const headers = [
    {
        title: 'Gegevens',
        key: '0',
        value: (i: any) => translatedImportOptionHeaders[i[0] as keyof ImportOptionHeaders],
        sortable: false
    },
    { title: 'Kolomnaam in importbestand (leeg = niet importeren)', value: '1', sortable: false }
];

function closeDialog() {
    file.value = undefined;
    isDialogOpen.value = false;
}

async function importActionPoints() {
    if (importingActionPoints.value === true) return;

    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    if (!options.value.headers.action) {
        toast.error('ColumnNameRequired', { columnName: translatedImportOptionHeaders['action'] });
        return;
    }

    importingActionPoints.value = true;
    const result = await actionPointStore.importActionPoints(props.organizationId, file.value as File, options.value);
    importingActionPoints.value = false;

    if (result.status === 200) closeDialog();

    emit('done');
}

watch(
    () => options.value.uploadFileToDocuments,
    (shouldUpload) => {
        if (!shouldUpload) options.value.shouldImportActionPoints = true;
    }
);
</script>

<template>
    <Dialog v-model="isDialogOpen" title="Actiepunten importeren" :isLoading="importingActionPoints">
        <div class="ml-3">
            <div class="mb-3">
                Om actiepunten succesvol te kunnen importeren is een Excel-bestand
                <span v-if="options.workSheetName"> met een werkblad genaamd "{{ options.workSheetName }}" </span>
                vereist.
            </div>

            <div class="mb-3">Daarnaast zijn de volgende kolommen vereist: {{ requiredColumns }}.</div>
            <v-form ref="form">
                <div class="v-col-12">
                    <v-file-input
                        v-model="file"
                        label="Bestand"
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel.sheet.macroenabled.12, application/vnd.ms-excel"
                        :rules="[required]"
                        required
                    />
                    <v-row>
                        <v-checkbox v-model="options.uploadFileToDocuments" label="Bestand naar RI&E module documenten uploaden" density="compact" />
                        <v-checkbox
                            v-if="options.uploadFileToDocuments"
                            v-model="options.shouldImportActionPoints"
                            label="Geen actiepunten importeren"
                            :true-value="false"
                            :false-value="true"
                            density="compact"
                        />
                    </v-row>
                    <details class="cursor-pointer">
                        <summary>Geavanceerd</summary>

                        <div class="mt-3">
                            <v-text-field v-model="options.workSheetName" label="Tabblad (niet ingevuld = eerste)" density="compact" />
                            <v-data-table :items="Object.entries(options.headers)" :headers="headers" :hide-default-footer="true">
                                <template v-slot:item.1="{ item }">
                                    <v-text-field
                                        v-model="options.headers[item[0] as keyof ImportOptionHeaders]"
                                        density="compact"
                                        hide-details
                                        :required="item[0] === 'subjectNumber'"
                                    />
                                </template>
                            </v-data-table>
                        </div>
                    </details>
                </div>
            </v-form>
        </div>
        <template #actions>
            <v-spacer />
            <CustomButton class="mr-2" @click="importActionPoints" text="Importeren" :loading="importingActionPoints" />
            <CustomButton color="red" @click="closeDialog" text="Annuleren" />
        </template>
    </Dialog>
</template>
